/**
 * legalVersions — the ONE place each published policy's effective date lives.
 *
 * WHY THIS EXISTS (2026-09-11). None of the three policy pages rendered a date
 * at all, while their own copy referred to one that was not there:
 *   Privacy.tsx  — "update the effective date above"      (there was no date above)
 *   Cookie.tsx   — "with a refreshed date stamp"          (there was no date stamp)
 * A privacy policy has to identify when it took effect — CalOPPA (Cal. B&P
 * §22575(b)(4)-(5)) says so outright — and a reader cannot tell whether a term
 * changed if nothing is dated. Three hand-typed dates would drift the way the
 * trial terms did before src/config/trialTerms.ts centralised those, so there
 * is one module and a gate (scripts/check-legal-effective-date.mjs) that fails
 * the build if a page stops rendering its date.
 *
 * WHEN TO BUMP: any change to the substance a reader relies on — what we
 * collect, who we share it with, what you can ask us to do, what you owe. Not
 * for typos, styling, or a reworded sentence that says the same thing. Bumping
 * is what tells a returning reader something actually changed, so a bump that
 * did not need to happen is its own small dishonesty.
 *
 * FORMAT: ISO `YYYY-MM-DD`, rendered through `formatEffectiveDate` so all three
 * pages read identically. Parsed as UTC on purpose — `new Date('2026-09-11')`
 * is UTC midnight, and formatting it in the viewer's local zone would show the
 * 10th to anyone west of Greenwich.
 */

export interface LegalDocumentVersion {
  /** ISO date the current text took effect. */
  readonly effectiveDate: string;
  /** What changed at that date — shown to nobody, read by whoever edits next. */
  readonly note: string;
}

export const LEGAL_VERSIONS = {
  privacy: {
    effectiveDate: '2026-10-18',
    note:
      'Made true to the platform as it runs (platform facts of 2026-10-16): prest0 is the controller of its own ' +
      'account, billing, website, sales and staff data and the firm\'s processor (a CCPA service provider) for the ' +
      'case and client content it places, in place of "data controller for every workspace"; the analytics-consent ' +
      'leftovers removed (no analytics product, pixel or tracker runs; Global Privacy Control honored); what is ' +
      'collected before an account named (guest chat 30 days after the last message, guest research 24 hours, the ' +
      'firm intake widget with no timer yet, the wait list, texts and calls to the company line); who receives texts ' +
      'and call audio (Twilio under its standard terms and data protection addendum, not a signed DPA; the model ' +
      'providers; Google Gemini Live; Prest0\'s own Slack for the company line); where data is stored, with its ' +
      'exceptions, and who reaches it from abroad (staff in Pakistan, sales representatives in Venezuela and ' +
      'Nicaragua), in place of "our processing is in the United States" and the unbacked standard-contractual-clauses ' +
      'sentence; the subprocessor list at prest0.ai/trust without a completeness claim; no-training stated as the ' +
      'gateway flags that run today and the 2026-10-31 target; server logs 30 days, admin-activity audit logs 400 ' +
      'days, backups cleared 7 days after a deletion; support records kept with the account; SMS consent records 4 ' +
      'years from the last text; Google Cloud\'s SOC 2 Type II and ISO/IEC 27001 named as Google\'s; children, ' +
      'contact (Privacy Officer) and 45-day response-time sections added. Earlier (2026-10-16): ' +
      'Security statements made true to the edge and the providers as they run (Miles, 2026-10-16: the ' +
      'security posture truthful within days): TLS in transit with TLS 1.3 for every browser that supports it, not ' +
      '"legacy protocols disabled" (the edge still accepts TLS 1.0); the no-training "guarantee" stated as the ' +
      'commitment it is, with enforcement at every provider targeted for October 31, 2026; "scoped api keys" ' +
      'removed (none exist); where our own audits stand pointed at the security page; the static copy\'s ' +
      '"configure retention" line replaced by the deletion sentence the page has. Earlier (2026-10-13): ' +
      'Deletion and location stated as the code runs them (SOC 2 Phase 1, 2026-10-13): a deletion request can be ' +
      'cancelled for 30 days and is carried out after that window, within 45 days of the request, except where a ' +
      'legal hold or legal, tax or regulatory duties require keeping records; the unbacked backup sentence and the ' +
      '"custom schedules per workspace" card removed; where customer production data is stored named (Google Cloud ' +
      'in the United States, us-central1, legacy workstations moving from us-east4). Earlier (2026-10-11): ' +
      'data-security section corrected to what runs (law-firm trust program, ' +
      'docs/PLAN-law-firm-trust-20261011.md): sign-in is Google or an email and ' +
      'password with permissions resolved per person and per message; the ' +
      'second-factor, device-check and risk-scoring claims are removed because ' +
      'none exists yet, and two-step verification is stated as planned; audit ' +
      'logs are append-only, ' +
      'not immutable; the 48-hour incident notice is a commitment whose delivery ' +
      'pipeline is being built; the HSTS claim is dropped because prest0.ai is ' +
      'served without the header today. Earlier (2026-09-11): cookies section ' +
      'corrected, analytics consent basis narrowed, GDPR/SCC framing scoped, the ' +
      'data-rights summary matched to what shipped.',
  },
  terms: {
    effectiveDate: '2026-10-18',
    note:
      'Made true to the platform as it runs (platform facts of 2026-10-16): Google Cloud\'s SOC 2 Type II and ' +
      'ISO/IEC 27001 named as Google\'s reports, not controls prest0 inherits; where data is stored, with its ' +
      'exceptions and remote staff access, and the subprocessor list without a completeness claim; the gateway\'s ' +
      'zero-retention flags beside the no-training target; backups cleared 7 days after a deletion; SMS consent ' +
      'records 4 years from the last text; the agent section describes what the platform does (Prest0n, routines, ' +
      'skills and workflows, not an agent builder); a contact section. The content licence narrowed to what prest0 ' +
      'does (content used only to provide, support and secure the services and as directed, never to train a model; ' +
      'aggregated measurements without content to run and improve the service), in place of "use ... to improve the ' +
      'platform"; the changes clause says how a revision really takes effect (asked in the product, applies from ' +
      'acceptance, 30 days\' notice before a change that materially reduces your rights), in place of 30 days\' notice ' +
      '"whenever practicable" with continued use as acceptance. Liability, indemnity, warranty and governing law ' +
      'unchanged. ' +
      'Earlier (2026-10-16): Security baseline made true (Miles, 2026-10-16): TLS in transit with TLS 1.3 where supported, the ' +
      'no-training commitment in place of a "guarantee", no "scoped API keys", the audits pointed at the security ' +
      'page, "verified security" now "documented security", and the acceptable-use guardrails naming controls that ' +
      'exist (permissions, ethical walls, legal holds) in place of redaction and retention timers that do not. ' +
      'Earlier (2026-10-13): Deletion timing and data location added to the security baseline (SOC 2 Phase 1, 2026-10-13), in the ' +
      "privacy policy's words. Earlier, two changes on 2026-10-11. Billing section made true to the platform " +
      '(signed-order program, Miles 2026-10-11): what a subscription includes ' +
      '(2,200 pr0 per Prest0n a month, good through the next month, no cash ' +
      'value); work is metered and the rate card is planning guidance; the ' +
      'wallet pays beyond the allocation and work stops when both are empty, ' +
      'never billed in arrears; no grace period on a failed charge, 30 days ' +
      'unpaid ends the plan; annual is a binding 12 months whose cancellation ' +
      'turns off renewal while its monthly payments continue; how plan changes ' +
      'take effect. The static copy, which still carried the pre-2026-09-11 ' +
      'one-paragraph terms, now matches. Security statements corrected to what ' +
      'runs (law-firm trust program): the account-security list asks for ' +
      'two-step on the Google account used to sign in and says two-step inside ' +
      'prest0 is planned; the second-factor, device-check and incident-report ' +
      'promises are removed; the 48-hour incident notice is stated as a ' +
      'commitment with the five facts it will carry and the pipeline being ' +
      'built; audit logs are append-only; HSTS dropped. Earlier (2026-09-11): ' +
      "billing section rewritten into California's Automatic Renewal Law " +
      'disclosures.',
  },
  cookie: {
    effectiveDate: '2026-10-18',
    note:
      'Made true to the site as it runs (2026-10-17): Stripe.js on the card and top-up forms (its fraud-prevention ' +
      'cookies, strictly necessary) and Google Calendar\'s booking page in the demo-booking window (Google\'s terms, ' +
      'loads only when opened) named, with the fonts\' hosts and the video players an insights post loads once ' +
      'play is pressed (YouTube\'s privacy-enhanced player, Vimeo with do-not-track, Loom); the consent-flag ' +
      'and load-balancer examples and the ' +
      'builder, dashboard and monetization tools that do not exist replaced by the storage the app keeps; server logs ' +
      '30 days, what they can hold, and admin-activity audit logs 400 days; Google Cloud\'s reports named as ' +
      'Google\'s. Earlier (2026-10-16): TLS without a version claim, the no-training commitment in place of a "guarantee", and the one ' +
      'page-speed reading the site sends (no identifier, no cookie) named (Miles, 2026-10-16). Earlier ' +
      '(2026-10-11): Security-controls section corrected (law-firm trust program): no consent ' +
      'banner or consent log exists, so neither is named; TLS 1.3 in transit ' +
      'without an HSTS claim; sign-in through Google or an email and password ' +
      'rather than a second factor nobody has; audit logs described as append-only. ' +
      'Earlier (2026-09-11): rewritten to describe reality — no analytics product, ' +
      'no advertising or social pixel and no A/B allocator; GPC honored.',
  },
  sms: {
    effectiveDate: '2026-10-18',
    note:
      'The SMS Terms\' first date in this file (2026-10-18): who receives a number, its texts and its calls named as ' +
      'the platform runs it (Twilio under its standard terms and data protection addendum, not a signed DPA; the ' +
      'model providers that write Prest0n\'s replies; Google Gemini Live for calls; Prest0\'s own Slack for the ' +
      'company line\'s texts and call transcripts); Prest0n\'s replies listed among what is sent; consent records ' +
      'kept 4 years from the last text sent under them. The React pages showed no date before; the static copy ' +
      '(public/sms-terms.html) said "last updated May 9, 2026" and now carries this date and the same words.',
  },
  referral: {
    effectiveDate: '2026-10-19',
    note:
      'The referral program\'s terms, first published (Malaika, 2026-10-19; docs/PLAN-referral-program-20261019.md): ' +
      'up to $1,000 for each new organization a person refers ($500 at its first paid month and $500 at its third, or ' +
      '$1,000 at once on an annual plan), credited to their wallet as withdrawable money; $500 of work units for 14 ' +
      'days for the person referred; one referral per organization, account, email and device; no self-referral; ' +
      'refunds and disputes take a reward back. This date is also the version a referrer accepts ' +
      '(REFERRAL_TERMS_VERSION in src/constants/referralVocabulary.ts, held equal by its parity test): changing the ' +
      'terms means a new date here and there, and every referrer accepts again before their link works.',
  },
} as const satisfies Record<'privacy' | 'terms' | 'cookie' | 'sms' | 'referral', LegalDocumentVersion>;

export type LegalDocumentKey = keyof typeof LEGAL_VERSIONS;

/** "september 11, 2026" — lowercase house voice, UTC so the date shown is the date recorded. */
export function formatEffectiveDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d))
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    // House voice is lowercase and no CSS transform may force it
    // (owner rule 2026-08-16, pinned by tests/policy/noUppercase.test.ts),
    // so the string is lowercased at the source rather than in styling.
    .toLowerCase();
}

/** The one string every policy page renders. */
export function effectiveDateLabel(key: LegalDocumentKey): string {
  return `effective ${formatEffectiveDate(LEGAL_VERSIONS[key].effectiveDate)}`;
}
